import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, ArrowLeft, Clock, Globe, Coins } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PreviewBar() {
  const { i18n } = useTranslation();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [isValidToken, setIsValidToken] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const token = searchParams.get('token');
  
  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidToken(false);
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/admin/preview/validate?token=${token}`);
        const data = await response.json();
        
        if (data.valid) {
          setIsValidToken(true);
          setRemainingSeconds(data.remaining_seconds);
        } else {
          setIsValidToken(false);
          // Redirect to admin if token invalid
          navigate('/admin');
        }
      } catch (err) {
        console.error('Token validation failed:', err);
        setIsValidToken(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    validateToken();
  }, [token, navigate]);
  
  // Countdown timer
  useEffect(() => {
    if (!isValidToken || remainingSeconds <= 0) return;
    
    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          // Token expired
          clearInterval(interval);
          navigate('/admin');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isValidToken, navigate]);
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Change language handler
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    // Update document direction for RTL
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };
  
  // Don't render if not in preview mode
  if (!token || isLoading || !isValidToken) {
    return null;
  }
  
  const currentLang = i18n.language || 'fr';
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] bg-[#1A1A1A] border-b border-zinc-700 py-2 px-4"
      data-testid="preview-bar"
    >
      <div className="container mx-auto flex items-center justify-between gap-4">
        {/* Left: Label */}
        <div className="flex items-center gap-3">
          <Badge className="bg-primary/20 text-primary border-primary/30 px-3 py-1">
            <Eye className="w-4 h-4 mr-2" />
            MODE PREVIEW
          </Badge>
          <span className="text-zinc-400 text-sm hidden sm:inline">Mutagim.shop</span>
        </div>
        
        {/* Center: Language & Currency switches */}
        <div className="flex items-center gap-4">
          {/* Language switch */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-zinc-500" />
            <div className="flex bg-zinc-800 rounded-lg p-0.5">
              {['fr', 'he', 'en'].map(lang => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    currentLang === lang
                      ? 'bg-primary text-white'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`preview-lang-${lang}`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          
          {/* Currency switch */}
          <div className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-zinc-500" />
            <div className="flex bg-zinc-800 rounded-lg p-0.5">
              {[
                { code: 'ILS', symbol: '₪' },
                { code: 'EUR', symbol: '€' },
                { code: 'USD', symbol: '$' }
              ].map(curr => (
                <button
                  key={curr.code}
                  onClick={() => setCurrency(curr.code)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    currency === curr.code
                      ? 'bg-primary text-white'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid={`preview-currency-${curr.code}`}
                >
                  {curr.symbol} {curr.code}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right: Timer & Back button */}
        <div className="flex items-center gap-4">
          {/* Timer countdown */}
          <div className="flex items-center gap-2 text-zinc-400" data-testid="preview-timer">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-mono">
              Expire dans <span className="text-white font-bold">{formatTime(remainingSeconds)}</span>
            </span>
          </div>
          
          {/* Back to admin button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin')}
            className="border-zinc-600 hover:bg-zinc-800"
            data-testid="preview-back-admin"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour admin
          </Button>
        </div>
      </div>
    </div>
  );
}

// Hook to check if in preview mode
export function usePreviewMode() {
  const [searchParams] = useSearchParams();
  const [isPreview, setIsPreview] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  
  const token = searchParams.get('token');
  
  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setIsPreview(false);
        setIsValidating(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/admin/preview/validate?token=${token}`);
        const data = await response.json();
        setIsPreview(data.valid === true);
      } catch {
        setIsPreview(false);
      } finally {
        setIsValidating(false);
      }
    };
    
    validate();
  }, [token]);
  
  return { isPreview, isValidating, token };
}

export default PreviewBar;
